import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useApiClient from "../hooks/useApiClient";

/**
 * Rejestr wyszukiwań na mapie konsultanta. Zapis: konsultant/admin/superadmin,
 * odczyt: admin/superadmin (sprawdzane w backendzie).
 */

export type ConsultantSearchKind = "address" | "address_select" | "precinct" | "parcel" | "plan_zone" | "parcel_click";

export const CONSULTANT_SEARCH_KIND_LABELS: Record<ConsultantSearchKind, string> = {
  address: "Adres",
  address_select: "Adres — wybrany wynik",
  precinct: "Obręb",
  parcel: "Działka",
  plan_zone: "Strefa planu",
  parcel_click: "Działka — klik na mapie",
};

export type ConsultantSearchInput = {
  procedureId?: string;
  kind: ConsultantSearchKind;
  query: string;
  result?: string;
  resultCount?: number;
};

export type ConsultantSearchLogEntry = {
  id: string;
  procedureId: string | null;
  userId: string;
  userEmail: string | null;
  userRole: string;
  kind: ConsultantSearchKind;
  query: string;
  result: string | null;
  resultCount: number | null;
  createdAt: string;
};

/** Zwraca funkcję zapisującą wyszukiwanie — fire-and-forget: błąd zapisu
 *  rejestru nie może przeszkadzać konsultantowi w pracy z mapą. */
export const useLogConsultantSearch = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  const { mutate } = useMutation<void, AxiosError, ConsultantSearchInput>({
    mutationKey: ["consultant-search-log", tenantId],
    mutationFn: async (entry) => {
      await privateApiClient.post(`/tenant/${tenantId}/consultant-search-log`, {
        ...entry,
        query: entry.query.slice(0, 300),
        result: entry.result?.slice(0, 500),
      });
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.warn("[consultant-search-log] nie zapisano wyszukiwania:", err.message);
    },
  });
  return (entry: ConsultantSearchInput) => {
    if (!tenantId || !entry.query.trim()) return;
    mutate(entry);
  };
};

export const useConsultantSearchLogQuery = (
  tenantId: string,
  params: { procedureId?: string; limit: number; offset: number },
) => {
  const { privateApiClient } = useApiClient();
  return useQuery<{ items: ConsultantSearchLogEntry[]; total: number }, AxiosError>({
    queryFn: async () =>
      (
        await privateApiClient.get(`/tenant/${tenantId}/consultant-search-log`, {
          params: {
            limit: params.limit,
            offset: params.offset,
            ...(params.procedureId ? { procedureId: params.procedureId } : {}),
          },
        })
      ).data,
    queryKey: ["consultant-search-log", tenantId, params.procedureId ?? "", params.limit, params.offset],
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
  });
};
